/**
 * designer-editor 文字属性面板
 *
 * 使用 editor-next 公共文字面板，并组合设计师端的 PPT 打标、文字动画与文字素材编辑能力。
 */
import React, { useState } from 'react';
import { Tabs } from '@meitu/saas-ui';
import { TextLayerPanel } from '@meitu/saas-editor-ui/es/panels/text-layer';
import { isEditAIPPTComponent, isEditAIPPTTemplate, isEditTextArt } from '@/hooks/fetch-template/editor-mode';
import { usePanelLayerId } from '@/hooks/use-panel-layer-id';
import { editor } from '@/instance';
import { isIntl } from '@/react-process-env';
import { useEditorModel } from '@/store/models/editor-model';
import { useFontMaterialModel } from '@/store/models/font-material-model';
import { useRecordModel } from '@/store/models/record-model';
import { useSdkModel } from '@/store/models/sdk-model';
import PPTMarkPanel from '../ppt-mark-panel';
import { TextArtConfig } from '../text-art-panel/text-art-panel';
import { useShapeTextData } from './hooks';
import { TextAnimationEntry } from './widgets';
import './style.less';

const pptMarkTabsList = [
    { name: '画笔', tabKey: 'bg' },
    { name: 'ppt打标', tabKey: 'pptMark' },
];

type RenderTabsProps = {
    setShowMaskContent: (flag: boolean) => void;
    isPPT?: boolean;
};

const RenderTabs: React.FC<RenderTabsProps> = ({ isPPT = false, setShowMaskContent }) => {
    const [tabIndex, setTabIndex] = useState(1);

    const handleTabClick = (key: string) => {
        setTabIndex(key === 'bg' ? 1 : 2);
        setShowMaskContent(key !== 'bg');
    };

    return (
        <div style={{ padding: '0 var(--gap-lg)' }}>
            <Tabs
                showContent={false}
                data={isPPT ? pptMarkTabsList : []}
                tabsWidth="228px"
                tabsHeight="34px"
                handleTabClick={handleTabClick}
                activeIndex={tabIndex}
            />
        </div>
    );
};

const isAIPPTMark = isEditAIPPTComponent() || isEditAIPPTTemplate();

const TextPanel: React.FC = () => {
    const layerId = usePanelLayerId();
    const shapeTextList = useShapeTextData();
    const fonts = useFontMaterialModel((state) => state.fonts);
    const listStyles = useFontMaterialModel((state) => state.listStyles);
    const artTexts = useFontMaterialModel((state) => state.artTexts);
    const highlights = useFontMaterialModel((state) => state.highlights);
    const theme = useEditorModel((state) => state.theme);
    const recordInfo = useRecordModel((state) => state.recordInfo);
    const customParams = useSdkModel((state) => state.customParams);
    const [showMaskContent, setShowMaskContent] = useState(false);

    if (!layerId) return null;

    return (
        <div>
            {isAIPPTMark ? (
                <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                    <RenderTabs setShowMaskContent={setShowMaskContent} isPPT />
                </div>
            ) : null}

            <div style={{ display: isAIPPTMark && showMaskContent ? 'none' : 'block' }}>
                {isEditTextArt() ? (
                    <div className="text-layer-panel__block">
                        <TextArtConfig layerId={layerId} />
                    </div>
                ) : (
                    <>
                        <TextLayerPanel
                            listStyles={listStyles}
                            artTexts={artTexts}
                            highlights={highlights}
                            theme={theme}
                            isIntl={isIntl}
                            editor={editor}
                            layerId={layerId}
                            recordId={recordInfo?.id}
                            isVIP
                            fonts={editor.services.font.toFontItems(fonts)}
                            shapeTextList={shapeTextList}
                            showHighlightEntry={!customParams.hideMask}
                            showArtTextEntry={false}
                            showEffectPresets={!customParams.hideTextEffect}
                            freeTextEffect={customParams.textEffectFree}
                            renderBrandTextsPopupTrigger={() => null}
                            renderTexturePanel={() => null}
                            renderTextRewriter={() => null}
                        />
                        <div className="text-layer-panel__block">
                            <TextAnimationEntry layerId={layerId} />
                        </div>
                    </>
                )}
            </div>

            {isAIPPTMark && showMaskContent ? <PPTMarkPanel editor={editor} key={layerId} /> : null}
        </div>
    );
};

export default React.memo(TextPanel);
